import React, { useEffect } from "react";
import { Calendar } from "antd";
import { Card, Headline, Flex, Paragraph } from "_styleguide";
import Footer from "../_components/Footer/Footer";
import { useLead } from "_contexts/useLead";
import dayjs from "dayjs";
import dayjsPluginUTC from "dayjs-plugin-utc";
import {
  IconChevronLeft,
  IconChevronRight,
  IconInfoCircle,
} from "@tabler/icons-react";
import { calculateStartDate } from "_assets/js/helpers";
import { hexToRgba } from "_assets/js/helpers";
import { useSettings } from "_contexts/useSettings";
import { isDarkColor } from "_helpers/colors";
import "./PaymentScheduling.scss";

dayjs.extend(dayjsPluginUTC);

function PaymentScheduling(props) {
  const { lead, updateLead } = useLead();
  const { settings } = useSettings();
  const pageSettings = props.settings?.settings;

  // Helper function to parse the date correctly
  const parseStartDate = (dateValue) => {
    if (!dateValue) return null;

    // If it's a string that looks like a timestamp
    if (typeof dateValue === "string" && /^\d+$/.test(dateValue)) {
      // Convert string timestamp to number and create dayjs object in UTC
      const timestamp = parseInt(dateValue);
      return dayjs(timestamp).utc();
    }

    // Otherwise, parse it and ensure it's in UTC
    return dayjs(dateValue).utc();
  };

  const disabledDate = (current) => {
    // Disable dates before today
    return current && current < dayjs().utc().startOf("day");
  };

  const chooseDate = (date) => {
    // Save as UTC ISO string
    const newPlan = {
      ...lead.plan,
      start_date: date.utc().toISOString(),
      payment_schedule: "monthly",
      weekend_payments: "monday",
    };
    updateLead("plan", newPlan);
  };

  useEffect(() => {
    if (!lead?.plan?.start_date) {
      const startDate = calculateStartDate();
      updateLead("plan", {
        ...lead.plan,
        start_date: startDate.utc().toISOString(),
        payment_schedule: "monthly",
        weekend_payments: "monday",
      });
    }
  }, []);

  const primaryOpacity = hexToRgba(settings?.primary_color || "#0f766e", 0.1);
  const primaryColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryColor) ? "#fff" : "#000";
  const secondaryColor = isDarkColor(primaryColor) ? primaryColor : "#000";

  return (
    <Card id="payment-scheduling">
      <div className="apply-form">
        <Headline>{pageSettings?.title}</Headline>
        {pageSettings?.subtitle ? (
          <Paragraph
            opacity={0.6}
            align="left"
            size="14px"
            dangerouslySetInnerHTML={{
              __html: pageSettings?.subtitle,
            }}
          />
        ) : null}

        <Flex vertical className="calendar-container">
          {lead?.plan?.start_date ? (
            <Calendar
              className="pp-calendar"
              defaultValue={parseStartDate(lead?.plan?.start_date)}
              disabledDate={disabledDate}
              fullscreen={false}
              onChange={chooseDate}
              style={{
                "--calendar-color": primaryColor,
                "--calendar-font-color": primaryFontColor,
              }}
              headerRender={({ value, type, onChange, onTypeChange }) => {
                let current = value.clone();
                return (
                  <div className="pp-calendar-header">
                    <h4>{current.format("MMMM YYYY")}</h4>
                    <div className="pp-calendar-header-actions">
                      <div
                        onClick={() => onChange(current.subtract(1, "month"))}
                      >
                        <IconChevronLeft />
                      </div>
                      <div onClick={() => onChange(current.add(1, "month"))}>
                        <IconChevronRight />
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          ) : null}
          <div className="info-banner" style={{ background: primaryOpacity }}>
            <IconInfoCircle style={{ color: secondaryColor }} />
            <span style={{ color: secondaryColor }}>
              Payment's due on the{" "}
              {parseStartDate(lead?.plan?.start_date)?.format("Do")} of each
              month.
            </span>
          </div>
        </Flex>
      </div>
      <Footer next={props?.settings?.next} settings={props?.settings} />
    </Card>
  );
}

export default PaymentScheduling;
